import React, { useState } from "react";
import { toast } from "sonner";
import { Phone } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function LoginDialog({ open, onOpenChange }) {
  const { login } = useAuth();
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [userType, setUserType] = useState("buyer");
  const [sent, setSent] = useState(false);
  const [demoOtp, setDemoOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async () => {
    if (!/^\d{10}$/.test(phone)) return toast.error("Enter a valid 10-digit mobile number");
    setLoading(true);
    try {
      const r = await api.post("/auth/send-otp", { phone });
      setSent(true);
      if (r.data.demo_otp) { setDemoOtp(r.data.demo_otp); toast.success(`Demo OTP: ${r.data.demo_otp}`); }
      else toast.success("OTP sent to your mobile");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send OTP"); } finally { setLoading(false); }
  };

  const verify = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    setLoading(true);
    try {
      const r = await api.post("/auth/verify-otp", { phone, otp, name, user_type: userType });
      login(r.data.token, r.data.user);
      toast.success(`Welcome, ${r.data.user.name}`);
      onOpenChange(false);
      setSent(false); setOtp(""); setPhone(""); setName("");
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 400) toast.error(detail || "Invalid or expired OTP");
      else toast.error("Login failed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl p-0 overflow-hidden">
        <div className="bg-kp-teal p-6 text-white">
          <DialogTitle className="text-2xl font-display font-bold">Welcome to Krishna Property</DialogTitle>
          <p className="text-sm text-white/80 mt-1">Login to save properties & post listings</p>
        </div>
        <div className="p-6 space-y-4">
          {!sent ? (
            <>
              <div>
                <label className="text-xs font-semibold text-slate-600">Mobile Number</label>
                <div className="flex items-center mt-1 border border-slate-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-kp-teal">
                  <span className="px-3 py-2.5 bg-slate-50 text-slate-700 text-sm border-r">+91</span>
                  <input type="tel" maxLength={10} value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g,""))} placeholder="10-digit mobile" className="flex-1 px-3 py-2.5 text-sm outline-none" data-testid="input-phone"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Name (optional)</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="input-name"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">I am a</label>
                  <Select value={userType} onValueChange={setUserType}>
                    <SelectTrigger data-testid="select-user-type"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buyer">Buyer / Tenant</SelectItem>
                      <SelectItem value="owner">Seller / Owner</SelectItem>
                      <SelectItem value="agent">Agent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={send} disabled={loading} className="btn-primary w-full rounded-full h-11 font-semibold" data-testid="btn-send-otp"><Phone size={16} className="mr-2"/> Send OTP</Button>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-600">OTP sent to <b>+91 {phone}</b>. {demoOtp && <span className="text-kp-maroon">Demo OTP: <b>{demoOtp}</b></span>}</p>
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="input-otp">
                  <InputOTPGroup>
                    {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i}/>)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setSent(false); setOtp(""); }} className="flex-1 rounded-full">Change Number</Button>
                <Button onClick={verify} disabled={loading} className="btn-primary flex-1 rounded-full font-semibold" data-testid="btn-verify-otp">Verify & Login</Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
